import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HealingIcon from '@mui/icons-material/Healing';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { DiscoveredRelease, DiscoveryKind } from '../api/types';
import { useApi } from '../api/useApi';
import {
  byProvider,
  composeSeason,
  contentsLabel,
  episodesLabel,
  groupHeading,
  groupVersions,
  missingEpisodes,
  numbers,
  type ComposedEpisode,
} from '../api/releaseContents';
import AsyncSection from '../components/AsyncSection';
import { formatSize } from '../components/media/formatSize';
import { languageName } from '../components/media/languageName';
import { episodeCode } from '../components/media/playbackTime';
import PageHeader from '../components/PageHeader';

/// Stands in where an indexer said nothing about how big a posting is. A dash rather than a
/// sentence, because it sits in a row of chips beside the ones that did say.
const UNKNOWN_SIZE = '—';

/// Searching the places that hold what the library does not - a Usenet indexer today, whatever
/// else grows a discovery provider later.
///
/// Every version an indexer returns is shown, grouped under its title. That is deliberate and
/// it is the opposite of what a download client does: a two-gigabyte encode and an eighty
/// gigabyte remux of the same film are both correct answers, and which of them is the right one
/// depends on the screen it will be watched on and the line it will come down. The server knows
/// neither, so it offers both and lets somebody choose.
export default function DiscoverPage() {
  const { t } = useTranslation();

  const providers = useApi(['discovery', 'providers'], api.discoveryProviders);
  const added = useApi(['discovery', 'added'], api.addedReleases);

  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<DiscoveryKind>('Any');
  const [busy, setBusy] = useState<string>();
  const [notice, setNotice] = useState<{ severity: 'success' | 'warning' | 'error'; text: string }>();

  const results = useApi(
    ['discovery', 'search', query, kind],
    () => api.discoverySearch(query, kind),
    { enabled: query.length > 0 },
  );

  const unusable = (providers.data ?? []).filter((provider) => !provider.isReady);
  const anyReady = (providers.data ?? []).some((provider) => provider.isReady);

  const act = async (id: string, run: () => Promise<void>) => {
    setBusy(id);
    setNotice(undefined);

    try {
      await run();
    } catch (error) {
      setNotice({ severity: 'error', text: (error as Error).message });
    } finally {
      setBusy(undefined);
    }
  };

  const add = (release: DiscoveredRelease) =>
    act(release.id, async () => {
      await api.addRelease(release.providerId, release.id);
      results.reload();
      added.reload();
      setNotice({ severity: 'success', text: t('discover.addedNotice', { name: release.name }) });
    });

  /// A season put together from single episodes, in one go. What could not be had is named
  /// rather than quietly missing: eleven episodes and a reason for the twelfth is the honest
  /// answer.
  const addBatch = (key: string, releases: DiscoveredRelease[]) =>
    act(key, async () => {
      let addedCount = 0;
      let failedCount = 0;

      for (const [providerId, batch] of byProvider(releases)) {
        const outcome = await api.addReleases(providerId, batch.map((release) => release.id));

        addedCount += outcome.added.length;
        failedCount += outcome.failures.length;
      }

      results.reload();
      added.reload();
      setNotice(
        failedCount === 0
          ? { severity: 'success', text: t('discover.composed.addedNotice', { total: addedCount }) }
          : { severity: 'warning', text: t('discover.season.partly', { added: addedCount, failed: failedCount }) },
      );
    });

  const remove = (release: DiscoveredRelease) =>
    act(release.id, async () => {
      await api.removeRelease(release.providerId, release.id);
      results.reload();
      added.reload();
    });

  const repair = (release: DiscoveredRelease) =>
    act(release.id, async () => {
      const report = await api.repairRelease(release.providerId, release.id);

      added.reload();
      setNotice({
        severity: report.isPlayable ? 'success' : 'warning',
        text: report.summary ?? t('discover.repair.done'),
      });
    });

  return (
    <Stack spacing={3}>
      <PageHeader title={t('discover.title')} description={t('discover.description')} />

      <AsyncSection loading={providers.loading} error={providers.error} onRetry={providers.reload}>
        {providers.data?.length === 0 ? (
          <Alert severity="info">{t('discover.noProviders')}</Alert>
        ) : null}

        {unusable.map((provider) => (
          <Alert key={provider.providerId} severity="warning" sx={{ mb: 1 }}>
            <Typography variant="body2">
              {t(provider.nameKey)}
              {': '}
              {provider.readinessKey ? t(provider.readinessKey) : provider.message}
            </Typography>
          </Alert>
        ))}
      </AsyncSection>

      {notice ? (
        <Alert severity={notice.severity} onClose={() => setNotice(undefined)}>
          {notice.text}
        </Alert>
      ) : null}

      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(typed.trim());
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: 'stretch' }}>
          <TextField
            fullWidth
            size="small"
            value={typed}
            disabled={!anyReady}
            onChange={(event) => setTyped(event.target.value)}
            label={t('discover.search.label')}
            placeholder={t('discover.search.placeholder')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button type="submit" variant="contained" disabled={!anyReady || typed.trim().length === 0}>
            {t('discover.search.action')}
          </Button>
        </Stack>
      </Box>

      <Tabs
        value={kind}
        onChange={(_, value: DiscoveryKind) => setKind(value)}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab value="Any" label={t('discover.kind.any')} />
        <Tab value="Movie" label={t('discover.kind.movie')} />
        <Tab value="Series" label={t('discover.kind.series')} />
      </Tabs>

      {query ? (
        <AsyncSection loading={results.loading} error={results.error} onRetry={results.reload}>
          {results.data?.length ? (
            <Stack spacing={1}>
              {results.data.map((title) => (
                <Accordion key={`${title.title}-${title.year ?? ''}`} disableGutters variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5, width: '100%', paddingInlineEnd: 1 }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>{title.title}</Typography>
                      {title.year ? (
                        <Typography variant="body2" color="text.secondary">
                          {title.year}
                        </Typography>
                      ) : null}
                      <Box sx={{ flexGrow: 1 }} />
                      <Chip
                        size="small"
                        label={t(title.versions.length === 1 ? 'discover.versions.one' : 'discover.versions.count', {
                          total: title.versions.length,
                        })}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <TitleVersions
                      titleKey={`${title.title}-${title.year ?? ''}`}
                      versions={title.versions}
                      busy={busy}
                      row={(release) => (
                        <ReleaseRow
                          key={release.id}
                          release={release}
                          busy={busy === release.id}
                          onAdd={() => void add(release)}
                          onRemove={() => void remove(release)}
                          onRepair={() => void repair(release)}
                        />
                      )}
                      onAdd={(release) => void add(release)}
                      onRemove={(release) => void remove(release)}
                      onRepair={(release) => void repair(release)}
                      onAddBatch={(key, releases) => void addBatch(key, releases)}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">{t('discover.noResults', { query })}</Typography>
          )}
        </AsyncSection>
      ) : null}

      <Typography variant="h6" component="h3">
        {t('discover.added.title')}
      </Typography>

      <AsyncSection loading={added.loading} error={added.error} onRetry={added.reload}>
        {added.data?.length ? (
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {added.data.map((release) => (
              <ReleaseRow
                key={release.id}
                release={release}
                busy={busy === release.id}
                onAdd={() => void add(release)}
                onRemove={() => void remove(release)}
                onRepair={() => void repair(release)}
              />
            ))}
          </Stack>
        ) : (
          <Typography color="text.secondary">{t('discover.added.empty')}</Typography>
        )}
      </AsyncSection>
    </Stack>
  );
}

/// The versions of one title, the way somebody reads them.
///
/// A film is a list of versions and stays one. A programme comes back as a few postings that
/// hold everything, some that hold a season and a great many that hold one episode each - so it
/// is broken up by season, in season order, and the single episodes of each season are put
/// together into that season rather than listed as a wall of release names.
function TitleVersions({
  titleKey,
  versions,
  busy,
  row,
  onAdd,
  onRemove,
  onRepair,
  onAddBatch,
}: {
  titleKey: string;
  versions: DiscoveredRelease[];
  busy: string | undefined;
  row: (release: DiscoveredRelease) => ReactNode;
  onAdd: (release: DiscoveredRelease) => void;
  onRemove: (release: DiscoveredRelease) => void;
  onRepair: (release: DiscoveredRelease) => void;
  onAddBatch: (key: string, releases: DiscoveredRelease[]) => void;
}) {
  const { t } = useTranslation();
  const groups = groupVersions(versions);

  if (groups.length <= 1 && !groups[0]?.releases.some((release) => release.contents?.scope === 'Episode')) {
    return (
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
        {versions.map(row)}
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      {groups.map((group) => {
        // Packs are listed as they are; the single episodes become the season they belong to.
        const packs = group.releases.filter((release) => release.contents?.scope !== 'Episode');
        const composed = composeSeason(group);

        return (
          <Accordion key={group.key} disableGutters variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600 }}>{groupHeading(group, t)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {group.episodes.length > 0
                    ? t(group.episodes.length === 1 ? 'discover.season.episode' : 'discover.season.episodes', {
                        total: group.episodes.length,
                        episodes: episodesLabel(group),
                      })
                    : t(group.releases.length === 1 ? 'discover.versions.one' : 'discover.versions.count', {
                        total: group.releases.length,
                      })}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Stack spacing={1.5}>
                {packs.length > 0 && (
                  <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>{packs.map(row)}</Stack>
                )}

                {composed.length > 0 && group.season !== undefined && (
                  <ComposedSeason
                    batchKey={`batch:${titleKey}:${group.key}`}
                    season={group.season}
                    composed={composed}
                    busy={busy}
                    onAdd={onAdd}
                    onRemove={onRemove}
                    onRepair={onRepair}
                    onAddBatch={onAddBatch}
                  />
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}

/// A season put together from single episodes: one row per episode, each with the version that
/// is taken for it and the others it can be swapped for, and a button that adds all of them.
///
/// Which version of an episode is right is somebody's choice as much as for a film - the German
/// dub of episode four may only exist in 720p - so every episode can be swapped on its own
/// without giving up the rest of the season.
function ComposedSeason({
  batchKey,
  season,
  composed,
  busy,
  onAdd,
  onRemove,
  onRepair,
  onAddBatch,
}: {
  batchKey: string;
  season: number;
  composed: ComposedEpisode[];
  busy: string | undefined;
  onAdd: (release: DiscoveredRelease) => void;
  onRemove: (release: DiscoveredRelease) => void;
  onRepair: (release: DiscoveredRelease) => void;
  onAddBatch: (key: string, releases: DiscoveredRelease[]) => void;
}) {
  const { t, i18n } = useTranslation();

  /// The versions somebody picked instead of the default, by episode.
  const [picked, setPicked] = useState<Record<number, string>>({});

  const chosenFor = (entry: ComposedEpisode) =>
    entry.versions.find((release) => release.id === picked[entry.episode]) ?? entry.chosen;

  // One posting can hold two episodes; it is fetched once.
  const chosen = [...new Map(composed.map((entry) => [chosenFor(entry).id, chosenFor(entry)])).values()];
  const missing = chosen.filter((release) => !release.isAdded);
  const size = chosen.reduce((total, release) => total + release.sizeInBytes, 0);
  const gaps = missingEpisodes(composed);
  const batchBusy = busy === batchKey;

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <Chip size="small" color="secondary" label={t('discover.composed.label')} />
        <Typography variant="body2">
          {t('discover.composed.description', { season })}
        </Typography>
        <Chip size="small" variant="outlined" label={formatSize(size) ?? UNKNOWN_SIZE} />
        <Box sx={{ flexGrow: 1 }} />
        {batchBusy ? <CircularProgress size={20} /> : null}
        {missing.length > 0 ? (
          <Button
            size="small"
            variant="contained"
            disabled={busy !== undefined}
            onClick={() => onAddBatch(batchKey, missing)}
          >
            {t('discover.composed.addAll', { total: missing.length })}
          </Button>
        ) : (
          <Chip size="small" color="success" variant="outlined" label={t('discover.composed.allAdded')} />
        )}
      </Stack>

      {gaps.length > 0 && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
          {t('discover.composed.gaps', { episodes: numbers(gaps) })}
        </Typography>
      )}

      <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} sx={{ mt: 1 }}>
        {composed.map((entry) => {
          const release = chosenFor(entry);
          const rowBusy = busy === release.id;

          return (
            <Stack key={entry.episode} spacing={0.5} sx={{ py: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
              >
                <Typography sx={{ fontWeight: 600, minWidth: 64 }}>{episodeCode(season, entry.episode)}</Typography>

                {entry.versions.length > 1 ? (
                  <TextField
                    select
                    size="small"
                    hiddenLabel
                    value={release.id}
                    disabled={busy !== undefined}
                    onChange={(event) => setPicked((current) => ({ ...current, [entry.episode]: event.target.value }))}
                    slotProps={{
                      htmlInput: { 'aria-label': t('discover.composed.choose', { episode: entry.episode }) },
                      select: { renderValue: () => versionSummary(release, t, i18n.language) },
                    }}
                    sx={{ flexGrow: 1, minWidth: 200, maxWidth: 520 }}
                  >
                    {entry.versions.map((version) => (
                      <MenuItem key={version.id} value={version.id} sx={{ whiteSpace: 'normal', maxWidth: 640 }}>
                        <Stack spacing={0.25}>
                          <Typography variant="body2">
                            {versionSummary(version, t, i18n.language)}
                            {version.isAdded ? ` · ${t('discover.badge.inLibrary')}` : ''}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ wordBreak: 'break-word', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
                          >
                            {version.name}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {versionSummary(release, t, i18n.language)}
                  </Typography>
                )}

                {rowBusy ? <CircularProgress size={20} /> : null}

                {release.isAdded ? (
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Chip size="small" color="success" variant="outlined" label={t('discover.badge.inLibrary')} />
                    <Tooltip title={t('discover.repair.action')}>
                      <span>
                        <IconButton size="small" disabled={busy !== undefined} onClick={() => onRepair(release)}>
                          <HealingIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('discover.remove')}>
                      <span>
                        <IconButton size="small" disabled={busy !== undefined} onClick={() => onRemove(release)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Button size="small" variant="outlined" disabled={busy !== undefined} onClick={() => onAdd(release)}>
                    {t('discover.add')}
                  </Button>
                )}
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ wordBreak: 'break-word', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
              >
                {release.name}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

/// What one version of an episode is told apart from the others by, in one line: picture, size,
/// soundtracks and source. The same things the chips of a full row say.
function versionSummary(release: DiscoveredRelease, t: (key: string) => string, locale: string): string {
  const parts: string[] = [];

  if (release.height) {
    parts.push(`${release.height}p`);
  }

  parts.push(formatSize(release.sizeInBytes) ?? UNKNOWN_SIZE);

  if (release.languages.length > 0) {
    parts.push(release.languages.map((code) => languageName(code, locale)).join(', '));
  } else if (release.isMultiLanguage) {
    parts.push(t('discover.multiLanguage'));
  }

  if (release.source) {
    parts.push(release.source);
  }

  return parts.join(' · ');
}

/// One version. The name is the release name exactly as the group that posted it wrote it -
/// it is the only description there is, and rewriting it would throw away what it says.
function ReleaseRow({
  release,
  busy,
  onAdd,
  onRemove,
  onRepair,
}: {
  release: DiscoveredRelease;
  busy: boolean;
  onAdd: () => void;
  onRemove: () => void;
  onRepair: () => void;
}) {
  const { t, i18n } = useTranslation();

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1, py: 1.25 }}
    >
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography
          variant="body2"
          sx={{ wordBreak: 'break-word', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
        >
          {release.name}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.5 }}>
          {/* How much of a programme is in it, first because it is the thing that decides
              whether the rest is worth reading. Nothing for a film, whose name says none. */}
          {contentsLabel(release.contents, t) ? (
            <Chip size="small" color="primary" label={contentsLabel(release.contents, t)} />
          ) : null}
          {release.height ? <Chip size="small" label={`${release.height}p`} /> : null}
          {/* The soundtracks, coloured so they read before the rest. Nothing is shown where
              nothing was said - an absent language is a gap in what the indexer wrote down,
              and a chip saying "unknown" would be reading it as a statement. */}
          {release.languages.map((code) => (
            <Chip key={code} size="small" color="primary" variant="outlined" label={languageName(code, i18n.language)} />
          ))}
          {release.isMultiLanguage && release.languages.length < 2 ? (
            <Chip size="small" color="primary" variant="outlined" label={t('discover.multiLanguage')} />
          ) : null}
          {release.subtitleLanguages.length ? (
            <Tooltip
              title={release.subtitleLanguages.map((code) => languageName(code, i18n.language)).join(', ')}
            >
              <Chip
                size="small"
                variant="outlined"
                label={t('discover.subtitles', { value: release.subtitleLanguages.length })}
              />
            </Tooltip>
          ) : null}
          <Chip size="small" variant="outlined" label={formatSize(release.sizeInBytes) ?? UNKNOWN_SIZE} />
          {release.source ? <Chip size="small" variant="outlined" label={release.source} /> : null}
          {release.publishedAt ? (
            <Chip
              size="small"
              variant="outlined"
              label={new Date(release.publishedAt).toLocaleDateString(i18n.language)}
            />
          ) : null}
          {release.isPlayable === false ? (
            <Chip size="small" color="warning" label={t('discover.condition.damaged')} />
          ) : null}
        </Stack>
        {release.condition ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {release.condition}
          </Typography>
        ) : null}
      </Box>

      {busy ? <CircularProgress size={20} /> : null}

      {release.isAdded ? (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('discover.repair.action')}>
            <span>
              <IconButton size="small" disabled={busy} onClick={onRepair}>
                <HealingIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('discover.remove')}>
            <span>
              <IconButton size="small" disabled={busy} onClick={onRemove}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ) : (
        <Button size="small" variant="outlined" disabled={busy} onClick={onAdd}>
          {t('discover.add')}
        </Button>
      )}
    </Stack>
  );
}

